import ks from "../ReUsableTS/kBrowserShared";
import { kEditableTableRow } from "./kEditableTableRow";
import { kHTMLEditableColType, kShouldGrowForTarget } from "./kEditableTableTypes";
import { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableColumn } from "./kEditableColumn";

// Member bodies of kEditableTable moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTable member keeps its signature and delegates here.
export class kEditableTableRowOps {
    public static AddEmptyRows(Self: kEditableTable, NumberOfRows:number): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            let Counter: number = 0;
            let CurrRowIndex: number = Self.Rows.Items.length;
            let CurrColIndex: number = 0;
            let CurrCol:kEditableColumn;
            if(Self.Table){
                for (Counter =0; Counter < NumberOfRows;Counter++) {
                    const NewRow: kEditableTableRow = new kEditableTableRow(CurrRowIndex);
                    NewRow.ParentTable = Self;
                    if(NewRow.Row){
                        CurrColIndex = 0;
                        for(CurrCol of Self.Columns.Items){
                            NewRow.AddCell(Self["_CreateEmptyRowCell"](CurrCol, CurrRowIndex, CurrColIndex));
                            CurrColIndex++;
                        }
                        Self.Table.appendChild(NewRow.Row);
                        Self.Rows.push(NewRow);
                        CurrRowIndex++;
                    }
                }
            }
            // A newly appended row joins the eligible set — re-page so it lands
            // on the correct page and the pager count updates (no-op unless a
            // filter is active or pagination is on).
            Self._ReapplyIfFiltering();
            // Keep the "+ new…" add-row hint on whichever row is now the trailing blank.
            Self["_ApplyAddRowHint"]();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps.AddEmptyRows");
            return ;
        }
    }

    /** Moved from kEditableTable._PaintBlankCellByType (size split); the class member delegates here. */
    public static _PaintBlankCellByType(Self: kEditableTable, NewCell: kEditableTableCell, CurrCol: kEditableColumn): void {
        if (ks.KErrorMode === true) { return; }
        try {
            switch (CurrCol.Type){
                case kHTMLEditableColType.Number:
                    NewCell.Cell.style.textAlign = "right";
                    NewCell.Cell.textContent = "0";
                    if (Self.BlankNumberCellsEmpty === true) { NewCell.Cell.textContent = ""; }
                    break;
                case kHTMLEditableColType.Currency:
                    NewCell.Cell.style.textAlign = "right";
                    NewCell.Cell.textContent = ks.LocalCurrencyNumber(0);
                    if (Self.BlankNumberCellsEmpty === true) { NewCell.Cell.textContent = ""; }
                    break;
                case kHTMLEditableColType.Checkbox:
                    NewCell.Cell.style.textAlign = "center";
                    NewCell.Cell.innerHTML = NewCell._CheckboxHtml(false);
                    break;
                case kHTMLEditableColType.Radio:
                    NewCell.Cell.style.textAlign = "center";
                    NewCell.Cell.innerHTML = NewCell._RadioHtml(false);
                    break;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps._PaintBlankCellByType");
        }
    }

    /** Moved from kEditableTable._ApplyAddRowHint (size split); the class member delegates here. */
    public static _ApplyAddRowHint(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.AddRowHint === "") { return; }
            for (const Row of Self.Rows.Items) {
                if (Row.Cells.length > 0) {
                    Row.Cells[0].Cell.classList.remove("kEditableTableAddHint");
                    Row.Cells[0].Cell.removeAttribute("data-addhint");
                }
            }
            if (Self.Rows.Items.length === 0) { return; }
            const Last: kEditableTableRow = Self.Rows.Items[Self.Rows.Items.length - 1];
            if (Last.Cells.length > 0) {
                Last.Cells[0].Cell.classList.add("kEditableTableAddHint");
                Last.Cells[0].Cell.setAttribute("data-addhint", Self.AddRowHint);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps._ApplyAddRowHint");
        }
    }

    public static InsertRowAbove(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (!Self.Table || Self.Rows.Items.length === 0) { return; }
            const InsertIdx: number = Self.Selection.RowIndexStart;
            const NewRow: kEditableTableRow = new kEditableTableRow(InsertIdx);
            NewRow.ParentTable = Self;
            if (!NewRow.Row) { return; }
            let CurrColIndex: number = 0;
            let CurrCol: kEditableColumn;
            for (CurrCol of Self.Columns.Items) {
                NewRow.AddCell(Self["_CreateInsertedRowCell"](CurrCol, InsertIdx, CurrColIndex));
                CurrColIndex++;
            }
            const RefRow: kEditableTableRow = Self.Rows.Items[InsertIdx];
            Self.Table.insertBefore(NewRow.Row, RefRow.Row);
            Self.Rows.Items.splice(InsertIdx, 0, NewRow);
            Self.UpdateRowNumbers();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps.InsertRowAbove");
        }
    }

    // True when the last row already carries user data — either committed cell
    // values (IsEmpty) OR the live text of an in-flight edit on that row whose
    // value has not yet been committed to CurrentValue (so "type into the last
    // row + Enter" is still recognised as content and grows). Lets navigation
    // decide whether stepping past the end warrants a fresh trailing blank
    // without spawning one on every keystroke against an empty trailing row.
    public static _LastRowHasContent(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.Rows.Items.length === 0) { return false; }
            const LastRow: kEditableTableRow = Self.Rows.Items[Self.Rows.Items.length - 1];
            if (ks.KNotNullOrUndefined(LastRow) && LastRow.IsEmpty() === false) { return true; }
            if (Self.CurrenEditableCell !== null
                && Self.CurrenEditableCell.RowIndex === Self.Rows.Items.length - 1
                && ks.KNotEmptyString(ks.Nz(Self.CurrenEditableCell.Cell.textContent))) {
                return true;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps._LastRowHasContent");
            return false;
        }
    }

    // SuppressGrowOnEmptyLastRow defaults false so paste / fill / programmatic
    // callers keep the historic always-grow-past-the-end behaviour. Cursor
    // navigation (SelectCellByIndexes) passes true to stop empty trailing rows
    // from multiplying — see kShouldGrowForTarget.
    public static GetCellByIndexes(Self: kEditableTable, RowIndex:number, ColIndex:number, SuppressGrowOnEmptyLastRow:boolean = false): kEditableTableCell {
        if (ks.KErrorMode === true) { return new kEditableTableCell ; }
        try {
            let CurrRow:kEditableTableRow;
            let CurrCell:kEditableTableCell = new kEditableTableCell();
            if(RowIndex >= Self.Rows.Items.length){
                if(kShouldGrowForTarget(RowIndex, Self.Rows.Items.length, Self.BlankRowsAllowed(), SuppressGrowOnEmptyLastRow, Self["_LastRowHasContent"]()) === true){
                    Self.AddEmptyRows(1);
                } else {
                    return CurrCell;
                }
            }

            if(RowIndex>=0){
                CurrRow = Self.Rows.Items[RowIndex];
                if(ColIndex < CurrRow.Cells.length && ColIndex >= 0){
                    CurrCell = CurrRow.Cells[ColIndex];
                }   
            }
            return CurrCell;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps.GetCellByIndexes");
            return  new kEditableTableCell;
        }
    }

    public static PasteHandler(Self: kEditableTable, Event:ClipboardEvent): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if (!Self.CurrenEditableCell) {
                const TopLeft: kEditableTableCell | null = Self.Selection.GetTopLeftCell();
                if (TopLeft) { Self.CurrenEditableCell = TopLeft; }
            }
            if(Self.CurrenEditableCell){
                Self["_AddPasted"]();
                Event.stopPropagation();
                Event.preventDefault();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps.PasteHandler");
            return ;
        }
    }

    public static RightClickPaste(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            Self.CurrenEditableCell = Self.RightClickCell;
            Self["_AddPasted"]();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps.RightClickPaste");
            return ;
        }
    }

    // Applies clipboard text to the grid. Named so the clipboard promise never
    // carries an anonymous callback (house rule) and so the paste path is
    // reachable from a test without a real clipboard.
    public static _OnClipboardText(Self: kEditableTable, Text: string): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if (!Self.CurrenEditableCell) { return; }
            Self.PasteInProgress = true;
            const Lines: string[] = Text.split('\n');
            if (Lines.length > 1) {
                Self["_PasteMultiCell"](Lines);
            } else {
                Self.CurrenEditableCell.SetNewValue(Text);
            }
            Self.PasteInProgress = false;
            ks.DoCallBack(Self.CellDirtiedCB, Self.CurrenEditableCell, false);
        } catch (e) {
            Self.PasteInProgress = false;
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps._OnClipboardText");
            return ;
        }
    }

    // Clipboard read rejected (permission denied, insecure context, empty
    // clipboard). Recovery is to clear the in-progress flag so the grid is not
    // left wedged mid-paste; the failure itself is then REPORTED — the old code
    // only console.error'd it, so a paste that did nothing looked like a grid
    // bug to the user with nothing on screen to explain it.
    public static _OnClipboardError(Self: kEditableTable, Err: any): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            Self.PasteInProgress = false;
            ks.KGlobalErrorHandler(Err, "kEditableTable._AddPasted.readText");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps._OnClipboardError");
            return ;
        }
    }

    public static _AddPasted(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if (!Self.CurrenEditableCell) { return; }
            Self.Table.focus();
            (navigator as any).clipboard.readText()
                .then(Self["_OnClipboardText"], Self["_OnClipboardError"]);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps._AddPasted");
            return ;
        }
    }

    public static _PasteMultiCell(Self: kEditableTable, Lines: (string | string[])[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (!Self.CurrenEditableCell) { return; }
            const RowsBelow: number = Self.Rows.Items.length - Self.CurrenEditableCell.RowIndex + 1;
            if (RowsBelow < Lines.length) {
                Self.AddEmptyRows(Lines.length - RowsBelow);
            }
            let CurrRowIndex: number = Self.CurrenEditableCell.RowIndex;
            let CurrColIndex: number = Self.CurrenEditableCell.ColumnIndex;
            for (let i: number = 0; i < Lines.length; i++) {
                const Line: string | string[] = Lines[i];
                let CurrRowValues: string[];
                if (typeof Line === "string") { CurrRowValues = Line.split('\t'); }
                else { CurrRowValues = Line; }
                if (CurrRowValues.length > 0) {
                    if (CurrRowIndex < Self.Rows.Items.length && Self.Rows.Items[CurrRowIndex].ReadOnly) {
                        CurrRowIndex++;
                        CurrColIndex = Self.CurrenEditableCell.ColumnIndex;
                        continue;
                    }
                    for (let j: number = 0; j < CurrRowValues.length; j++) {
                        if (CurrRowIndex < Self.Rows.Items.length) {
                            const CurrRow: kEditableTableRow = Self.Rows.Items[CurrRowIndex];
                            if (CurrColIndex < CurrRow.Cells.length) {
                                CurrRow.Cells[CurrColIndex].SetNewValue(CurrRowValues[j].trim());
                                CurrColIndex++;
                            }
                        }
                    }
                    CurrRowIndex++;
                    CurrColIndex = Self.CurrenEditableCell.ColumnIndex;
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRowOps._PasteMultiCell");
        }
    }
}
